import { Bell, LogOut, Menu, Search } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { navSections } from "./Sidebar";

interface TopbarProps {
	sidebarCollapsed: boolean;
	onMenuClick: () => void;
}

const crumbFor = (pathname: string) => {
	for (const section of navSections) {
		for (const item of section.items) {
			if (pathname === item.path || pathname.startsWith(`${item.path}/`)) {
				return `${section.label} · ${item.label}`;
			}
		}
	}
	return "";
};

export const Topbar = ({ sidebarCollapsed, onMenuClick }: TopbarProps) => {
	const { user, logout } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();

	const handleLogout = async () => {
		await logout();
		navigate("/login");
	};

	return (
		<header
			className={`fixed top-0 right-0 z-30 h-16 border-zinc-200 border-b bg-white/90 backdrop-blur transition-all duration-300 ease-in-out ${
				sidebarCollapsed ? "left-[70px]" : "left-[250px]"
			}`}
		>
			<div className="flex h-full items-center justify-between pr-4">
				<div className="flex min-w-0 items-center gap-2">
					<button
						type="button"
						onClick={onMenuClick}
						aria-label="Toggle navigation"
						className="flex h-16 items-center px-4 text-header-item transition-colors hover:text-ink"
					>
						<Menu className="h-4 w-4" strokeWidth={2.5} />
					</button>
					<p className="truncate font-medium text-[10.5px] text-zinc-400 uppercase tracking-[0.18em]">
						{crumbFor(location.pathname)}
					</p>
				</div>

				<div className="flex items-center gap-3">
					<div className="relative hidden sm:block">
						<Search className="absolute top-1/2 left-3.5 h-4 w-4 -translate-y-1/2 text-zinc-400" />
						<input
							type="text"
							placeholder="Search (Ctrl+K)"
							className="w-64 rounded-full border border-zinc-200 bg-white py-2 pr-4 pl-10 text-[13px] text-ink transition-colors placeholder:text-zinc-400 focus:border-zinc-400 focus:outline-none"
						/>
					</div>

					<button
						type="button"
						className="relative rounded-full border border-zinc-200 p-2 text-zinc-500 transition-colors hover:border-zinc-300 hover:text-ink"
					>
						<Bell className="h-4 w-4" />
						<span className="absolute top-1.5 right-1.5 size-1.5 rounded-full bg-red-500" />
					</button>

					<div className="mx-1 h-8 w-px bg-zinc-200" />

					<div className="flex items-center gap-3">
						{user?.picture ? (
							<img
								src={user.picture}
								alt={user.name}
								className="h-9 w-9 rounded-full border border-zinc-200 object-cover"
							/>
						) : (
							<div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-night">
								<span className="font-semibold text-brand text-sm">
									{user?.name?.charAt(0) || "A"}
								</span>
							</div>
						)}
						<div className="hidden min-w-0 md:block">
							<p className="truncate font-medium text-[13px] text-ink">
								{user?.name || "Admin"}
							</p>
							<p className="truncate text-[11px] text-muted">
								{user?.email || ""}
							</p>
						</div>
					</div>

					<button
						type="button"
						onClick={handleLogout}
						className="group rounded-full p-2 transition-colors hover:bg-red-50"
						title="Sign out"
					>
						<LogOut className="h-4 w-4 text-zinc-400 transition-colors group-hover:text-danger" />
					</button>
				</div>
			</div>
		</header>
	);
};
